import React, { Suspense, useState, useRef, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { 
  useGLTF, 
  OrbitControls, 
  PivotControls, 
  Text, 
  Html, 
  Environment, 
  ContactShadows,
  TransformControls,
  Bounds,
  useBounds
} from '@react-three/drei'

// 1. The GLB Model Component
function Model() {
  const { scene } = useGLTF('https://backend.shieldxsports.com/uploads/category/customize/10171020251760685987.glb')
  return <primitive object={scene} scale={1.5} position={[0, -1, 0]} />
}

// 2. Simplified Editable Text Component with proper border
function ShirtText({ 
  id, 
  content, 
  position, 
  scale = 0.15, 
  rotation = [0, 0, 0],
  onUpdate,
  onDelete,
  onCopy
}) {
  const [text, setText] = useState(content)
  const [isEditing, setIsEditing] = useState(false)
  const [isSelected, setIsSelected] = useState(false)
  const [localPosition, setLocalPosition] = useState(position)
  const [localScale, setLocalScale] = useState(scale)
  const textRef = useRef()

  const handleTextUpdate = (newText) => {
    setText(newText)
    if (onUpdate) {
      onUpdate(id, { content: newText, position: localPosition, fontSize: localScale })
    }
  }

  const handlePositionUpdate = (newPosition) => {
    setLocalPosition(newPosition)
    if (onUpdate) {
      onUpdate(id, { content: text, position: newPosition, fontSize: localScale })
    }
  }

  return (
    <>
      <PivotControls 
        anchor={[0, 0, 0]}
        depthTest={false}
        scale={0.6}
        lineWidth={2}
        fixed={true}
        onDrag={(local) => {
          const newPos = [local[12], local[13], local[14]]
          handlePositionUpdate(newPos)
        }}
      >
        <group 
          position={localPosition} 
          rotation={rotation}
          onClick={(e) => {
            e.stopPropagation()
            setIsSelected(true)
          }}
        >
          {/* Text with proper outline using outline properties */}
          <Text
            ref={textRef}
            fontSize={localScale}
            color="#ffffff"
            outlineWidth={0.02}
            outlineColor="#000000"
            outlineOpacity={1}
            anchorX="center"
            anchorY="middle"
            font="https://fonts.gstatic.com/s/raleway/v22/1Ptxg8zYS_SKggPN4iEgvnHyvveLxVvaorCIPrQ.ttf"
          >
            {text}
          </Text>
        </group>
      </PivotControls>

      {/* Control buttons for the text */}
      {isSelected && (
        <Html position={[localPosition[0], localPosition[1] + 0.3, localPosition[2]]}>
          <div style={{
            display: 'flex',
            gap: '5px',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '8px',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)'
          }}>
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: '6px 12px',
                background: 'rgba(59, 130, 246, 0.8)',
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold'
              }}
            >
              Edit
            </button>
            <button
              onClick={() => {
                if (onCopy) onCopy(id)
                setIsSelected(false)
              }}
              style={{
                padding: '6px 12px',
                background: 'rgba(34, 197, 94, 0.8)',
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold'
              }}
            >
              Copy
            </button>
            <button
              onClick={() => {
                if (onDelete) onDelete(id)
              }}
              style={{
                padding: '6px 12px',
                background: 'rgba(239, 68, 68, 0.8)',
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold'
              }}
            >
              Delete
            </button>
            <button
              onClick={() => setIsSelected(false)}
              style={{
                padding: '6px 12px',
                background: 'rgba(107, 114, 128, 0.8)',
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold'
              }}
            >
              Close
            </button>
          </div>
        </Html>
      )}

      {/* Edit modal */}
      {isEditing && (
        <Html center>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.95)',
            padding: '20px',
            borderRadius: '15px',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            minWidth: '300px',
            zIndex: 1000
          }}>
            <h3 style={{ color: 'white', marginTop: 0, marginBottom: '15px' }}>
              Edit Text
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', color: 'white', marginBottom: '5px' }}>
                Text:
              </label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '5px',
                  color: 'white',
                  fontSize: '14px'
                }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: 'white', marginBottom: '5px' }}>
                Size: {localScale.toFixed(2)}
              </label>
              <input
                type="range"
                min="0.05"
                max="0.4"
                step="0.01"
                value={localScale}
                onChange={(e) => {
                  const newScale = parseFloat(e.target.value)
                  setLocalScale(newScale)
                  if (onUpdate) {
                    onUpdate(id, { content: text, position: localPosition, fontSize: newScale })
                  }
                }}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setIsEditing(false)
                  setIsSelected(false)
                }}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(239, 68, 68, 0.8)',
                  border: 'none',
                  borderRadius: '5px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleTextUpdate(text)
                  setIsEditing(false)
                  setIsSelected(false)
                }}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(34, 197, 94, 0.8)',
                  border: 'none',
                  borderRadius: '5px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </Html>
      )}
    </>
  )
}

// 3. Add Text Button Component (Floating on shirt)
function AddTextButton() {
  const [isAdding, setIsAdding] = useState(false)
  const [newText, setNewText] = useState('NEW TEXT')
  const [buttonPosition, setButtonPosition] = useState([0, -0.5, 0.4])

  return (
    <PivotControls 
      anchor={[0, 0, 0]}
      depthTest={false}
      scale={0.5}
      lineWidth={2}
      fixed={true}
    >
      <group position={buttonPosition}>
        <Html transform distanceFactor={5}>
          <button
            onClick={() => setIsAdding(true)}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              borderRadius: '25px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
            }}
          >
            + ADD TEXT
          </button>
        </Html>
      </group>

      {/* Add Text Modal */}
      {isAdding && (
        <Html center>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.95)',
            padding: '25px',
            borderRadius: '15px',
            border: '2px solid rgba(102, 126, 234, 0.5)',
            minWidth: '350px',
            zIndex: 1000,
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
          }}>
            <h3 style={{ 
              color: 'white', 
              marginTop: 0, 
              marginBottom: '20px',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Add New Text
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: 'white', marginBottom: '8px', fontWeight: '500' }}>
                Enter Text:
              </label>
              <input
                type="text"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Enter your text here..."
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '2px solid rgba(102, 126, 234, 0.3)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(102, 126, 234, 0.7)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)'}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => setIsAdding(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(239, 68, 68, 0.9)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  transition: 'transform 0.2s',
                  minWidth: '100px'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // This would be handled by parent component
                  setIsAdding(false)
                }}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #34d399 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  transition: 'transform 0.2s',
                  minWidth: '100px'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Add Text
              </button>
            </div>
          </div>
        </Html>
      )}
    </PivotControls>
  )
}

// Main Component
export default function SportClothConfigurator() {
  const [textElements, setTextElements] = useState([
    { id: 1, content: "DRAG ME", position: [0, 0.5, 0.4], fontSize: 0.15 },
    { id: 2, content: "SAMPLE", position: [0.5, 0.3, 0.4], fontSize: 0.12 }
  ])
  const [nextId, setNextId] = useState(3)

  const updateTextElement = (id, updates) => {
    setTextElements(elements =>
      elements.map(element =>
        element.id === id ? { ...element, ...updates } : element
      )
    )
  }

  const deleteTextElement = (id) => {
    setTextElements(elements => elements.filter(element => element.id !== id))
  }

  const copyTextElement = (id) => {
    const elementToCopy = textElements.find(element => element.id === id)
    if (elementToCopy) {
      const newElement = {
        ...elementToCopy,
        id: nextId,
        position: [elementToCopy.position[0] + 0.2, elementToCopy.position[1], elementToCopy.position[2]]
      }
      setTextElements([...textElements, newElement])
      setNextId(nextId + 1)
    }
  }

  const addTextElement = (content) => {
    const newElement = {
      id: nextId,
      content: content || "NEW TEXT",
      position: [0, 0.2, 0.4],
      fontSize: 0.15
    }
    setTextElements([...textElements, newElement])
    setNextId(nextId + 1)
  }

  return (
    <div style={{ 
      width: '100vw', 
      height: '100vh', 
      background: '#111'
    }}>
      <Canvas camera={{ position: [0, 0, 4], fov: 50 }}>
        <ambientLight intensity={0.7} />
        <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} />
        
        <Suspense fallback={
          <Html center>
            <div style={{ color: 'white', fontSize: '18px', textAlign: 'center' }}>
              Loading 3D Model...
            </div>
          </Html>
        }>
          {/* Your GLB Model */}
          <Model />

          {/* All Text Elements */}
          {textElements.map((text) => (
            <ShirtText
              key={text.id}
              id={text.id}
              content={text.content}
              position={text.position}
              scale={text.fontSize}
              onUpdate={updateTextElement}
              onDelete={deleteTextElement}
              onCopy={copyTextElement}
            />
          ))}

          {/* Add Text Button */}
          <AddTextButton />

          {/* Environment & Shadows */}
          <Environment preset="studio" />
          <ContactShadows 
            opacity={0.4} 
            scale={12} 
            blur={2} 
            far={15} 
            resolution={512} 
            color="#000000" 
            position={[0, -1, 0]}
          />
        </Suspense>

        <OrbitControls 
          makeDefault 
          enableDamping
          dampingFactor={0.05}
          minDistance={2}
          maxDistance={10}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI / 1.5}
        />
      </Canvas>

      {/* Simple UI Overlay */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        color: 'white',
        background: 'rgba(0, 0, 0, 0.6)',
        padding: '15px',
        borderRadius: '10px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        maxWidth: '300px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Instructions:</h3>
        <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '14px' }}>
          <li>Click on text to select it</li>
          <li>Drag the controls to move text</li>
          <li>Use Edit/Copy/Delete buttons</li>
          <li>Click "ADD TEXT" to add new text</li>
        </ul>
      </div>

      {/* Add Text Button in UI */}
      <button
        onClick={() => addTextElement()}
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 30px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          borderRadius: '25px',
          color: 'white',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: 'bold',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
          zIndex: 100
        }}
      >
        + Add New Text
      </button>
    </div>
  )
}